import React, { useCallback, useEffect, useState } from 'react';

import { LinearProgress } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';

import { Province, ProvinceOp } from '../../../@types/KingdomResponse';
import { ProvinceLite } from '../../../@types/State';
import { autosizeOptions } from '../../../Constants';
import { useIntelState } from '../../../context/IntelState';
import { effects } from '../../../Effects';
import RestUtilities from '../../../services/RestUtilities';
import OpsToolBar from '../components/OpsToolBar';
import { StyledOpsDataGrid } from '../components/StripedDataGrid';
import { getSvgItemWithTooltip } from '../helpers/colHelpers';

type TickRow = {
  damage: number;
  success: number;
  fail: number;
};
type Row = {
  [key: `tick_${number}`]: TickRow;
  opType: string;
  total: number;
  damage: number;
  success: number;
  fail: number;
};

const renderResult = (row: Row, i: number) => {
  const success = row[`tick_${i}`].success;
  const fail = row[`tick_${i}`].fail;
  return (
    <div style={{ width: '100%' }}>
      <div style={{ textAlign: 'right', height: '20px' }}>
        {row[`tick_${i}`].damage || ''}
      </div>
      <div>
        {success > 0 && (
          <span style={{ marginRight: '2px', color: 'SpringGreen' }}>
            &#10004; {success}
          </span>
        )}
        {fail > 0 && <span style={{ color: 'Tomato' }}>&#10006; {fail}</span>}
      </div>
    </div>
  );
};

const renderTotal = (row: Row) => {
  return (
    <div style={{ width: '100%' }}>
      <div style={{ textAlign: 'right', height: '20px' }}>
        {row.total || ''}
      </div>
      <div>
        {row.success > 0 && (
          <span style={{ marginRight: '2px', color: 'SpringGreen' }}>
            &#10004; {row.success}
          </span>
        )}
        {row.fail > 0 && (
          <span style={{ color: 'Tomato' }}>&#10006; {row.fail}</span>
        )}
      </div>
    </div>
  );
};

const getTickCol = (i: number): GridColDef => {
  return {
    field: `tick_${i}`,
    headerName: i === 0 ? 'Today' : i === 1 ? 'Yesterday' : `-${i} days`,
    description:
      i === 0
        ? 'Ops occuring this tick'
        : i === 1
        ? 'Ops occuring last tick'
        : `Ops occuring -${i} ticks ago`,
    width: 90,
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    filterable: false,
    renderCell: (params: GridRenderCellParams) => renderResult(params.row, i),
  };
};

const defaultCols: GridColDef[] = [
  {
    field: 'opIcon',
    headerName: '',
    width: 30,
    renderCell: (params: GridRenderCellParams) => {
      const opType = params.row.opType.replaceAll('_', ' ');
      const effect =
        effects[opType.replaceAll(' ', '').replaceAll("'", '').toUpperCase()];
      return effect
        ? getSvgItemWithTooltip(effect.svg, effect.displayName)
        : opType;
    },
  },
  {
    field: 'opType',
    headerName: 'Op Name',
    width: 120,
    renderCell: (params: GridRenderCellParams) => (
      <div style={{ textTransform: 'capitalize' }}>
        {params.row.opType.toLowerCase()}
      </div>
    ),
  },
  {
    field: 'total',
    headerName: 'Total',
    description: 'Total Ops that happened in last 24 ticks',
    width: 90,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params: GridRenderCellParams) => renderTotal(params.row),
  },
];

function uniqueFilter(value: string, index: number, self: string[]) {
  return self.indexOf(value) === index;
}

function groupBy<T>(arr: T[], fn: (item: T) => any) {
  return arr.reduce<Record<string, T[]>>((prev, curr) => {
    const groupKey = fn(curr);
    const group = prev[groupKey] || [];
    group.push(curr);
    return { ...prev, [groupKey]: group };
  }, {});
}

function reduce(arr: ProvinceOp[], field: string) {
  return arr.reduce((t, v) => t + (v[field] ? v[field] : 0), 0);
}

const EnemyOpsGrid = () => {
  const { intelState, intelDispatch } = useIntelState();
  const [data, setData] = useState<ProvinceOp[] | undefined>(undefined);
  const [rows, setRows] = useState<Row[] | undefined>(undefined);
  const [opTypes, setOpTypes] = useState<string[] | undefined>(undefined);
  const [opType, setOpType] = useState<string>('All Ops');
  const [columns, setColumns] = useState<GridColDef[] | undefined>(undefined);
  const [province, setProvince] = useState<ProvinceLite | undefined>(undefined);

  const provinces: ProvinceLite[] | undefined =
    intelState.enemyData?.provinces.map((x: Province) => {
      return {
        id: x.id,
        name: x.name,
        race: x.race,
        slot: `[${x.id}]`,
      } as ProvinceLite;
    });

  const handleOpTypeChange = (event: any) => {
    setOpType(event.target.value);
  };

  const handleProvinceChange = (event: any) => {
    const prov: ProvinceLite | undefined = provinces?.find(
      (x: ProvinceLite) => x.id == event.target.value
    );
    if (prov) {
      setProvince(prov);
    } else {
      setProvince(undefined);
    }
  };

  const getData = useCallback(async () => {
    intelDispatch({ type: 'SET_LOADING', payload: true });
    const response = await RestUtilities.get(
      `Kingdom/v1/EnemyKingdomOps?server=${intelState.server}&location=${intelState.enemyKd}`
    );
    if (response.ok) {
      const resp: ProvinceOp[] = response.content;
      setData(resp);
    } else {
      setData(undefined);
    }
    intelDispatch({ type: 'SET_LOADING', payload: false });
  }, [intelDispatch, intelState.server, intelState.enemyKd]);

  useEffect(() => {
    const unique = data ? Array.from(new Set(data.map((op) => op.name))) : [];
    if (unique.length) {
      setOpTypes(unique.sort());
    }
  }, [data]);

  useEffect(() => {
    getData();
    const interval = setInterval(() => {
      getData();
    }, 30000);
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [getData]);

  useEffect(() => {
    if (!data) return;
    const uniqueDates = data.map((item) => item.utoDate).filter(uniqueFilter);
    const dataSet: any[] = [];
    const newColumns = [...defaultCols];
    uniqueDates.forEach((d, i) => {
      newColumns.push(getTickCol(i));
    });
    setColumns(newColumns);
    let filteredData = data;
    // Apply DropDown Filters
    if (province) {
      filteredData = filteredData.filter(
        (x: ProvinceOp) => x.slot === province.id
      );
    }
    if (opType !== 'All Ops') {
      filteredData = filteredData?.filter((o: ProvinceOp) => o.name === opType);
    }
    const opGrouped = groupBy(filteredData, (po: ProvinceOp) => po.opType);
    for (const [opType, rows] of Object.entries(opGrouped)) {
      const newRow: Row = {
        opType: opType.replaceAll('_', ' '),
        total: 0,
        success: 0,
        fail: 0,
        damage: 0,
      };
      newRow.total = reduce(rows, 'damage');
      newRow.success = rows.filter((x) => x.result === 1).length;
      newRow.fail = rows.filter((x) => x.result === 0).length;
      uniqueDates.forEach((date: string, index: number) => {
        const rowsOnDate = rows.filter((r) => r.utoDate === date);
        let damage = reduce(rowsOnDate, 'damage');
        if (opType === 'PROPAGANDA') {
          let extraSpec = 0;
          rowsOnDate.forEach((r) => {
            if (r.name == 'Specialists' && r.gain) {
              extraSpec += r.gain;
            }
          });
          damage += extraSpec;
        }
        const success = rowsOnDate.filter((r) => r.result === 1).length;
        const fail = rowsOnDate.filter((r) => r.result === 0).length;
        const dateRow: TickRow = {
          damage,
          success,
          fail,
        };
        newRow[`tick_${index}`] = dateRow;
      });
      dataSet.push(newRow);
    }
    setRows(dataSet);
  }, [data, province, opType]);

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {rows && columns && (
        <StyledOpsDataGrid
          rowHeight={60}
          columns={columns}
          rows={rows}
          loading={intelState.loading}
          getRowId={(row) => row.opType}
          autosizeOptions={autosizeOptions}
          autosizeOnMount={true}
          disableColumnMenu={true}
          hideFooterPagination
          hideFooter
          showToolbar
          slots={{
            toolbar: OpsToolBar,
            columnMenuUnsortIcon: null,
          }}
          slotProps={{
            toolbar: {
              provinces,
              province,
              handleProvinceChange,
              opTypes,
              handleOpTypeChange,
              opType,
            },
          }}
        />
      )}
    </div>
  );
};

export default EnemyOpsGrid;
